import React, { useState } from "react";
import { Link } from "react-router-dom";
import { resolveImg } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

const imgCache = new Set();

export default function ProductCard({ product, priority = false, dark = false, contain = false }) {
  const { lang, t } = useLang();
  const img1 = product.images?.[0];
  const img2 = product.images?.[1];
  const src1 = img1 ? resolveImg(img1) : null;
  const [loaded, setLoaded] = useState(() => (src1 ? imgCache.has(src1) : false));
  const [err, setErr] = useState(false);
  const soldOut = product.availability === "sold_out";
  const name = lang === "ar" ? product.name_ar : (product.name_en || product.name_ar);
  const markLoaded = () => { if (src1) imgCache.add(src1); setLoaded(true); };
  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`} className="group block">
      <div className={`relative aspect-[4/5] overflow-hidden ${contain ? "bg-[#EFE7D8]" : "bg-surface"}`}>
        {(!loaded || err) && (
          <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#F1EADD] via-[#EFE7D8] to-[#E6DCC9] animate-pulse" data-testid={`card-skeleton-${product.id}`} />
        )}
        {img1 && !err && (
          <img key={`${product.id}-${img1}`} src={src1} alt={name}
            loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"} decoding="async"
            ref={(el) => { if (el && el.complete && el.naturalWidth > 0) markLoaded(); }}
            onLoad={markLoaded} onError={() => { setErr(true); setLoaded(true); }}
            className={`relative z-[1] w-full h-full ${contain ? "object-contain" : "object-cover"} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} md:transition-transform md:group-hover:scale-[1.04]`} />
        )}
        {err && (
          <div className="absolute inset-0 z-[1] flex items-center justify-center text-text-muted text-xs" data-testid={`card-imgerr-${product.id}`}>الصورة غير متاحة</div>
        )}
        {img2 && !err && loaded && (
          <img src={resolveImg(img2)} alt="" loading="lazy" decoding="async"
            className={`hidden md:block absolute inset-0 z-[2] w-full h-full ${contain ? "object-contain" : "object-cover"} opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out`} />
        )}
        {soldOut && (
          <span className="absolute top-2 start-2 z-10 bg-[#141110] text-[#F9F6F0] text-[10px] tracking-[0.15em] uppercase px-2 py-1">{t("outOfStock")}</span>
        )}
        <span className="absolute bottom-2 start-2 z-10 bg-[#F9F6F0]/85 backdrop-blur-sm text-[#141110] text-[10px] px-2 py-0.5">{lang === "ar" ? "تفصيل حسب المقاس" : "Made to measure"}</span>
      </div>
      <div className="pt-3 pb-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className={`font-display text-base md:text-lg leading-snug transition-colors ${dark ? "text-[#F9F6F0] group-hover:text-[#E4C88A]" : "group-hover:text-accent"}`}>{name}</h3>
          <span className={`text-[10px] tracking-[0.15em] uppercase shrink-0 ${dark ? "text-[#E4C88A]" : "text-accent"}`}>{product.sku}</span>
        </div>
        <p className={`font-num text-[15px] mt-0.5 ${dark ? "text-[#F9F6F0]/75" : "text-text-muted"}`} dir="ltr">{formatKWD(product.price, lang)}</p>
      </div>
    </Link>
  );
}
